import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import { useAppSettingsStore } from '../../../../stores/appSettingsStore';
import { BD } from '../bookDemoTheme';

const LANGUAGES: { code: string; labelKey: string; ariaKey: string }[] = [
  { code: 'en', labelKey: 'landing.language.en', ariaKey: 'landing.language.selectEn' },
  { code: 'nl', labelKey: 'landing.language.nl', ariaKey: 'landing.language.selectNl' },
];

/**
 * The landing page's language dropdown, restyled for this dark header. Same mechanism
 * (`appSettingsStore.setLanguage` → i18n + cookie) and the same `landing.language.*` copy;
 * only the surface colours differ, which is why this is a sibling rather than a prop-driven
 * variant of `landingpage/sections/LanguageSwitch` — that one is hardcoded light and shared
 * styling props would have meant threading five class names through it.
 */
const DemoLanguageSwitch: React.FC = () => {
  const { t } = useTranslation();
  const { language, setLanguage } = useAppSettingsStore();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const activeCode = language?.startsWith('nl') ? 'nl' : 'en';

  useEffect((): (() => void) | undefined => {
    if (!isOpen) return undefined;
    const onMouseDown = (e: MouseEvent): void => {
      if (!containerRef.current?.contains(e.target as Node)) setIsOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return (): void => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen]);

  return (
    <div ref={containerRef} className="relative w-fit">
      <button
        type="button"
        onClick={() => setIsOpen(prev => !prev)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={t('landing.language.label')}
        className={`h-10 px-3 rounded-lg border flex items-center gap-1 text-[14px] font-semibold transition-colors ${BD.ghostButton}`}
      >
        {t(`landing.language.${activeCode}`)}
        <ChevronDown
          size={16}
          aria-hidden="true"
          className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div
          role="listbox"
          aria-label={t('landing.language.label')}
          className={`absolute right-0 z-40 mt-2 min-w-[96px] rounded-xl border p-1 ${BD.cardBg} ${BD.borderStrong} ${BD.cardShadow}`}
        >
          {LANGUAGES.map(lang => {
            const isActive = lang.code === activeCode;
            return (
              <button
                key={lang.code}
                type="button"
                role="option"
                aria-selected={isActive}
                aria-label={t(lang.ariaKey)}
                onClick={() => {
                  setLanguage(lang.code);
                  setIsOpen(false);
                }}
                className={`block w-full rounded-lg px-3 py-1.5 text-left text-[14px] leading-5 font-medium transition-colors duration-150 ${
                  isActive ? `${BD.selectedBg} ${BD.textPrimary}` : `${BD.textSecondary} ${BD.hoverTile}`
                }`}
              >
                {t(lang.labelKey)}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DemoLanguageSwitch;
